/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { walletService } from '../../services/api';
import type { TransactionGroup } from '../../types';
import { Tag } from '../console/ui';
import { ChevronDownIc } from '../console/icons';
import {
  DisplayCurrency,
  parseDescription,
  PRODUCTS,
  productKeyOf,
  serverGroupDetail,
  serverGroupResource,
  txAmountValue,
  formatSigned,
} from './billingUtils';

const EXPAND_LIMIT = 100;

const groupKey = (g: TransactionGroup) => `${g.day}:${g.type}:${g.reference_type}:${g.reference_id || g.transaction_id}`;

/** Raw lines behind one group, fetched when the row is expanded. */
const GroupLines: React.FC<{ g: TransactionGroup; currency: DisplayCurrency; rate: number }> = ({ g, currency, rate }) => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['transactions', 'groupLines', groupKey(g)],
    queryFn: () =>
      walletService
        .getTransactionsFiltered({
          reference_id: g.reference_id,
          reference_type: [g.reference_type],
          type: [g.type],
          from: g.day,
          to: g.day,
          page_size: EXPAND_LIMIT,
        })
        .then((r) => r.data),
    staleTime: 60_000,
  });
  const cell = '!bg-ps-chrome text-ps-muted text-[13px]';
  if (isLoading || isError) {
    return (
      <tr>
        <td className="!bg-ps-chrome" />
        <td colSpan={4} className={cell}>
          {isLoading ? 'Loading charges…' : 'Couldn’t load the individual charges.'}
        </td>
      </tr>
    );
  }
  const txs = data?.transactions || [];
  return (
    <>
      {txs.map((tx) => {
        const p = parseDescription(tx.description);
        return (
          <tr key={tx.id}>
            <td className="!bg-ps-chrome" />
            <td colSpan={3} className={`${cell} max-w-[600px] truncate`} title={tx.description}>
              {format(new Date(tx.created_at), 'HH:mm')}
              {p.spec ? ` · ${p.spec}` : ''}
            </td>
            <td className={`${cell} text-right`}>{formatSigned(tx.type, txAmountValue(tx, currency, rate), currency)}</td>
          </tr>
        );
      })}
      {(data?.total || 0) > txs.length && (
        <tr>
          <td className="!bg-ps-chrome" />
          <td colSpan={4} className={cell}>
            Showing the latest {txs.length} of {(data?.total || 0).toLocaleString('en-IN')} charges. The CSV export has every line.
          </td>
        </tr>
      )}
    </>
  );
};

/**
 * Charges table backed by GET /wallet/transactions/grouped: one row per
 * resource per day, aggregated server-side. Multi-charge rows expand to the
 * raw lines on demand.
 */
const GroupedChargesTable: React.FC<{
  groups: TransactionGroup[];
  currency: DisplayCurrency;
  rate: number;
  footer?: React.ReactNode;
  /** Column order: "date-first" (transactions) or "resource-first" (overview). */
  variant?: 'date-first' | 'resource-first';
  expandable?: boolean;
}> = ({ groups, currency, rate, footer, variant = 'date-first', expandable = true }) => {
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const dateFirst = variant === 'date-first';

  const rows: React.ReactNode[] = [];
  groups.forEach((g) => {
    const key = groupKey(g);
    const productKey = productKeyOf(g.product, g.type);
    const prod = PRODUCTS[productKey];
    const { resource, mono } = serverGroupResource(g);
    const detail = serverGroupDetail(g, productKey);
    const canExpand = expandable && g.count > 1 && !!g.reference_id;
    const isOpen = !!open[key];
    const type = g.type === 'credit' ? 'credit' : 'debit';

    const date = <td className="text-ps-muted">{format(new Date(`${g.day}T00:00:00`), 'd MMM')}</td>;
    const resourceCell = (
      <td className={`max-w-[360px] truncate ${mono ? 'font-mono' : ''}`} title={g.description || resource}>
        {canExpand ? (
          <button
            type="button"
            onClick={() => setOpen((s) => ({ ...s, [key]: !s[key] }))}
            aria-expanded={isOpen}
            className={`inline-flex items-center gap-1.5 max-w-full text-left hover:text-ps-accent ${mono ? 'font-mono' : ''}`}
          >
            <span className="truncate">{resource}</span>
            <span
              aria-hidden="true"
              className={`text-ps-subtle transition-transform [&>svg]:w-4 [&>svg]:h-4 ${isOpen ? 'rotate-180' : ''}`}
            >
              <ChevronDownIc />
            </span>
            <span className="sr-only">{isOpen ? 'Hide' : 'Show'} individual charges</span>
          </button>
        ) : (
          resource
        )}
      </td>
    );
    const product = (
      <td>
        <Tag tone={prod.tone}>{prod.label}</Tag>
      </td>
    );
    rows.push(
      <tr key={key}>
        {dateFirst ? (
          <>
            {date}
            {resourceCell}
            {product}
          </>
        ) : (
          <>
            {resourceCell}
            {product}
            {date}
          </>
        )}
        <td className="text-ps-muted max-w-[380px] truncate" title={detail}>
          {detail}
        </td>
        <td className={`text-right ${type === 'credit' ? 'text-ps-ok' : 'text-ps-text'}`}>
          {formatSigned(type, currency === 'INR' ? g.total_amount : g.total_amount / (rate || 1), currency)}
        </td>
      </tr>,
    );
    if (canExpand && isOpen) rows.push(<GroupLines key={`${key}:lines`} g={g} currency={currency} rate={rate} />);
  });

  return (
    <div className="ps-card overflow-x-auto">
      <table className="ps-tbl">
        <thead>
          {dateFirst ? (
            <tr>
              <th>Date</th>
              <th>Resource</th>
              <th>Product</th>
              <th>Detail</th>
              <th className="!text-right">Amount</th>
            </tr>
          ) : (
            <tr>
              <th>Resource</th>
              <th>Product</th>
              <th>Period</th>
              <th>Detail</th>
              <th className="!text-right">Amount</th>
            </tr>
          )}
        </thead>
        <tbody>{rows}</tbody>
      </table>
      {footer && <div className="px-4 py-3 border-t border-ps-raised text-[13px] text-ps-subtle">{footer}</div>}
    </div>
  );
};

export default GroupedChargesTable;
